import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  SerialProfile,
  SerialProfileStatus,
} from '../../serial-profile.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

export interface SerialProfileFormData {
  name: string;
  description?: string;
  status: SerialProfileStatus;
  lotRequired: boolean;
  expiryRequired: boolean;
  serialRequired: boolean;
}

@Component({
  selector: 'app-serial-profile-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AppButtonComponent,
    InputTextModule,
    TextareaModule,
    SelectModule,
    CheckboxModule,
  ],
  templateUrl: './serial-profile-dialog.component.html',
  styleUrl: './serial-profile-dialog.component.css',
})
export class SerialProfileDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translateService = inject(TranslateService);

  showErrors = signal(false);
  originalStatus: SerialProfileStatus | null = null;

  // Check if editing existing profile
  get isEditMode(): boolean {
    return !!this.config?.data?.profile;
  }

  // Get status options with disabled state
  get statusOptions() {
    const status = this.originalStatus || 'ACTIVE';
    return [
      {
        label: this.translateService.instant(
          'settings.serial_profile.status.active'
        ),
        value: 'ACTIVE' as SerialProfileStatus,
      },
      {
        label: this.translateService.instant(
          'settings.serial_profile.status.inactive'
        ),
        value: 'INACTIVE' as SerialProfileStatus,
      },
      {
        label: this.translateService.instant(
          'settings.serial_profile.status.archived'
        ),
        value: 'ARCHIVED' as SerialProfileStatus,
        disabled: status === 'ACTIVE',
      },
    ];
  }

  formData: SerialProfileFormData = {
    name: '',
    description: '',
    status: 'ACTIVE',
    lotRequired: false,
    expiryRequired: false,
    serialRequired: false,
  };

  constructor() {
    // Check if editing existing profile
    if (this.config?.data?.profile) {
      const profile: SerialProfile = this.config.data.profile;
      this.originalStatus = profile.status || 'ACTIVE';
      this.formData = {
        name: profile.name || '',
        description: profile.description || '',
        status: this.originalStatus,
        lotRequired: profile.lotRequired ?? false,
        expiryRequired: profile.expiryRequired ?? false,
        serialRequired: profile.serialRequired ?? false,
      };
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onConfirm() {
    if (!this.formData.name?.trim()) {
      this.showErrors.set(true);
      return;
    }

    // If onConfirm callback exists, call it (for edit mode with archive confirmation)
    if (typeof this.config.data?.onConfirm === 'function') {
      const isConfirmed = await this.config.data.onConfirm(this);
      if (isConfirmed) {
        this.dialogRef.close(this.formData);
      }
    } else {
      // For add mode or edit mode without confirmation, close directly with formData
      this.dialogRef.close(this.formData);
    }
  }
}
